import React, { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { ChevronLeft, Download, Copy, Check, Share2 } from 'lucide-react'
import './Certificate.css'

const SITE_ORIGIN = 'https://app.techbench.it'
const VERIFY_BASE = 'https://techbench.it/verify'
const CERT_KIND_LABELS = {
  'foundation-ready': {
    title: 'IT Foundation',
    titleAccent: 'Ready.',
    tierLabel: 'Foundation',
    tierNumber: 'Tier 01',
    stamp: 'Ready',
    docKind: 'Certificate of Completion',
    description: 'Completed all three Foundation tracks — demonstrating technical competence, professional judgement, and AI literacy at the level required to begin a career in IT.',
  },
}

function formatDate(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  } catch {
    return ''
  }
}

export function CertificatePage() {
  const { user } = useAuth()
  const [searchParams] = useSearchParams()

  const [cert, setCert] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [copied,      setCopied     ] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const certRef = useRef(null)
  const isNew = searchParams.get('new') === '1'

  // Fetch cert + profile on mount
  useEffect(() => {
    if (!user) return
    let cancelled = false

    async function load() {
      setLoading(true)

      try {
        // Profile (for the name on the cert)
        const { data: profileRow } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .single()

        // Credential — prefer URL-provided ID if present (fresh issue), else
        // fall back to the user's most recent foundation credential.
        // maybeSingle() returns null without erroring when no row matches —
        // that's exactly what we want for users who don't have a cert yet.
        const urlId = searchParams.get('id')
        let credQuery = supabase.from('credentials').select('*').eq('user_id', user.id)
        if (urlId) {
          credQuery = credQuery.eq('id', urlId)
        } else {
          credQuery = credQuery.eq('kind', 'foundation-ready')
        }
        const { data: credRow } = await credQuery.maybeSingle()

        if (!cancelled) {
          setProfile(profileRow)
          setCert(credRow)
        }
      } catch (err) {
        // Network glitch or unexpected failure — log it and let the empty
        // state render rather than spinning forever. The user can refresh
        // or navigate elsewhere.
        console.error('Failed to load certificate:', err)
        if (!cancelled) {
          setProfile(null)
          setCert(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [user, searchParams])

  // Auto-scroll up on mount
  useEffect(() => { window.scrollTo({ top: 0 }) }, [])

  if (loading) {
    return (
      <div className="cert-page-loading">
        <div className="cert-page-loading__spinner" />
      </div>
    )
  }

  // No credential? Guide them back.
  if (!cert) {
    return (
      <div className="cert-page-empty">
        <h1 className="cert-page-empty__title">You have no TechBench certificates yet.</h1>
        <p className="cert-page-empty__sub">
          Complete all three Foundation tracks and the capstone to earn your IT Foundation Ready certificate.
        </p>
        <Link to="/dashboard" className="cert-page-empty__btn">
          <ChevronLeft size={14} />Back to course
        </Link>
      </div>
    )
  }

  const kind   = CERT_KIND_LABELS[cert.kind] || CERT_KIND_LABELS['foundation-ready']
  const name   = cert.recipient_name || profile?.full_name || 'TechBench learner'
  const issued = formatDate(cert.issued_at)
  const verifyUrl = `${VERIFY_BASE}/${cert.id}`

  function handleCopy() {
    navigator.clipboard.writeText(verifyUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    })
  }

  async function handleDownload() {
    if (downloading || !certRef.current) return
    setDownloading(true)

    try {
      // Dynamic imports — keeps the bundle lazy until first use (~400KB)
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf'),
      ])

      const el = certRef.current

      // Capture at 2x resolution for crisp print quality
      const canvas = await html2canvas(el, {
        scale:           2,
        useCORS:         true,
        allowTaint:      false,
        backgroundColor: '#ffffff',
        logging:         false,
        // Override colours to white-theme equivalents so the dark cert
        // renders as the clean white PDF version (mirrors @media print)
        onclone: (doc) => {
          const cloned = doc.querySelector('.cert')
          if (!cloned) return

          // Background and border
          cloned.style.background    = '#ffffff'
          cloned.style.border        = '1px solid #d1d5db'
          cloned.style.boxShadow     = 'none'
          cloned.style.borderRadius  = '0'

          // Dot-grid overlay → light version
          const before = doc.createElement('style')
          before.textContent = `
            .cert::before { background-image: radial-gradient(circle, rgba(0,0,0,0.05) 1px, transparent 1px) !important; }
            .cert__rail { border-right: 1px solid #e5e7eb !important; }
            .cert-mark__word { color: #0a0c10 !important; }
            .cert-rail__eyebrow, .cert-issuance__label, .cert-doc-kind,
            .cert-awarded__label, .cert-authenticity, .cert-seal__caption,
            .cert-id__label, .cert-seal__tier, .cert-wordmark__tagline { color: #6b7280 !important; }
            .cert-rail__dash { background: #6b7280 !important; }
            .cert-seal__ring-outer { border-color: #9ca3af !important; }
            .cert-seal__ring-inner { border-color: #d1d5db !important; }
            .cert-seal__label, .cert-seal__caption strong, .cert-authenticity strong { color: #1f2937 !important; }
            .cert-seal__stamp { color: #6b7280 !important; }
            .cert-id { border-top-color: #d1d5db !important; }
            .cert-id__value { color: #0a0c10 !important; }
            .cert-issuance__value, .cert-credential-name, .cert-awarded__name,
            .cert-wordmark { color: #0a0c10 !important; }
            .cert-description { color: #4b5563 !important; }
            .cert-description strong { color: #0a0c10 !important; }
            .cert-awarded { border-top-color: #e5e7eb !important; }
            .cert__footer { border-top-color: #e5e7eb !important; }
            .cert-track { border-color: #d1d5db !important; color: #4b5563 !important; background: transparent !important; }
          `
          doc.head.appendChild(before)
        },
      })

      // A4 landscape in mm
      const A4_W = 297
      const A4_H = 210
      const margin = 8 // mm padding on each side

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })

      const availW = A4_W - margin * 2
      const availH = A4_H - margin * 2

      // Scale image to fit inside available area, preserving aspect ratio
      const imgW = canvas.width
      const imgH = canvas.height
      const ratio = Math.min(availW / imgW, availH / imgH)
      const finalW = imgW * ratio
      const finalH = imgH * ratio

      // Centre on the page
      const x = margin + (availW - finalW) / 2
      const y = margin + (availH - finalH) / 2

      const imgData = canvas.toDataURL('image/png')
      pdf.addImage(imgData, 'PNG', x, y, finalW, finalH)

      const safeName = (name || 'TechBench').replace(/[^a-z0-9]/gi, '-').toLowerCase()
      pdf.save(`techbench-certificate-${safeName}.pdf`)

    } catch (err) {
      console.error('PDF generation failed:', err)
      // Graceful fallback — open print dialog
      window.print()
    } finally {
      setDownloading(false)
    }
  }

  function handleLinkedIn() {
    const certUrl = encodeURIComponent(`${SITE_ORIGIN}/certificate?id=${cert.id}`)
    const issueDate = cert.issued_at ? new Date(cert.issued_at) : new Date()
    const params = new URLSearchParams({
      startTask: 'CERTIFICATION_NAME',
      name: 'IT Foundation Ready',
      organizationName: 'TechBench',
      issueYear: String(issueDate.getFullYear()),
      issueMonth: String(issueDate.getMonth() + 1),
      certUrl: certUrl,
      certId: cert.id,
    })
    window.open(`https://www.linkedin.com/profile/add?${params.toString()}`, '_blank')
  }

  return (
    <div className="cert-page">

      <div className="cert-page__header">
        <Link to="/learn" className="cert-page__back">
          <ChevronLeft size={14} />Course
        </Link>
        {isNew && (
          <div className="cert-page__new-badge mono">Just issued</div>
        )}
      </div>

      {isNew && (
        <div className="cert-page__celebrate">
          <div className="cert-page__celebrate-eyebrow mono">Foundation complete</div>
          <h1 className="cert-page__celebrate-title">You built something real.</h1>
          <p className="cert-page__celebrate-sub">
            Three tracks. Hundreds of lessons. A capstone that put all of it together.
            This is yours now.
          </p>
        </div>
      )}

      {!isNew && (
        <div className="cert-page__heading">
          <div className="cert-page__heading-eyebrow mono">Your certificate</div>
          <h1 className="cert-page__heading-title">IT Foundation Ready</h1>
        </div>
      )}

      {/* === THE CERTIFICATE (dark preview) === */}
      <Certificate
        ref={certRef}
        name={name}
        credentialId={cert.id}
        issuedAt={issued}
        kind={kind}
      />

      {/* === ACTIONS === */}
      <div className="cert-actions">
        <button className="cert-action cert-action--primary" onClick={handleDownload} disabled={downloading}>
          <Download size={16} />
          {downloading ? 'Generating…' : 'Download PDF'}
        </button>
        <button className="cert-action cert-action--secondary" onClick={handleCopy}>
          {copied ? <><Check size={16} />Copied</> : <><Copy size={16} />Copy verify link</>}
        </button>
        <button className="cert-action cert-action--secondary" onClick={handleLinkedIn}>
          <Share2 size={16} />
          Add to LinkedIn
        </button>
      </div>

      <div className="cert-page__verify-note mono">
        Verify at <span>{verifyUrl.replace(/^https?:\/\//, '')}</span>
      </div>

    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════
// The Certificate component itself — the dark asymmetric design.
// Takes props so it can later be reused as a preview card on the Dashboard.
// ════════════════════════════════════════════════════════════════════════
export const Certificate = React.forwardRef(function Certificate({ name, credentialId, issuedAt, kind }, ref) {
  const verifyPath = `techbench.it/verify/${credentialId}`
  return (
    <div ref={ref} className="cert" role="img" aria-label={`${kind.title} ${kind.titleAccent} certificate for ${name}`}>
      <div className="cert__grid">

        {/* Left rail */}
        <aside className="cert__rail">
          <div>
            <div className="cert-mark">
              <div className="cert-mark__icon">
                <svg viewBox="0 0 100 100" fill="none" stroke="#0a0c10" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="42" y1="22" x2="58" y2="22"/>
                  <rect x="18" y="32" width="64" height="40" rx="6" ry="6"/>
                  <line x1="36" y1="46" x2="66" y2="46"/>
                  <line x1="36" y1="58" x2="54" y2="58"/>
                </svg>
              </div>
              <span className="cert-mark__word">TechBench</span>
            </div>
            <div className="cert-rail__eyebrow mono">
              <span className="cert-rail__dash" />TechBench Credential
            </div>
          </div>

          <div className="cert-seal-wrap">
            <div className="cert-seal">
              <div className="cert-seal__ring-accent" />
              <div className="cert-seal__ring-outer" />
              <div className="cert-seal__ring-inner" />
              <div className="cert-seal__content">
                <div className="cert-seal__tier mono">{kind.tierNumber}</div>
                <div className="cert-seal__label">{kind.tierLabel}</div>
                <div className="cert-seal__divider" />
                <div className="cert-seal__stamp mono">{kind.stamp}</div>
              </div>
            </div>
            <div className="cert-seal__caption mono">
              Awarded on completion of <strong>all three</strong> Foundation tracks.
            </div>
          </div>

          <div className="cert-id">
            <div className="cert-id__label mono">Credential ID</div>
            <div className="cert-id__value mono">{credentialId}</div>
            <div className="cert-id__verify mono">{verifyPath}</div>
          </div>
        </aside>

        {/* Body */}
        <main className="cert__body">

          <div className="cert-issuance">
            <div className="cert-issuance__item">
              <div className="cert-issuance__label mono">Issued</div>
              <div className="cert-issuance__value">{issuedAt}</div>
            </div>
            <div className="cert-issuance__item">
              <div className="cert-issuance__label mono">Valid</div>
              <div className="cert-issuance__value">No expiry</div>
            </div>
          </div>

          <div className="cert-doc-kind mono">{kind.docKind}</div>

          <h1 className="cert-credential-name">
            {kind.title}<br />
            {kind.titleAccent}
          </h1>

          <div className="cert-awarded">
            <div className="cert-awarded__label mono">Awarded to</div>
            <div className="cert-awarded__name">{name}</div>
          </div>

          <div className="cert-description">
            {kind.description}
          </div>

          <div className="cert-tracks">
            <span className="cert-track"><span className="cert-track__dot" />IT Foundation</span>
            <span className="cert-track"><span className="cert-track__dot" />Human Skills</span>
            <span className="cert-track"><span className="cert-track__dot" />AI Foundations</span>
          </div>

          <div className="cert__footer">
            <div className="cert-authenticity mono">
              Issued by <strong>TechBench</strong>. Verifiable at the URL on the left. Unique to the holder. Not transferable.
            </div>
            <div className="cert-wordmark">
              TechBench
              <span className="cert-wordmark__tagline mono">Where IT careers are built</span>
            </div>
          </div>

        </main>
      </div>
    </div>
  )
})
